import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlattener,
  MatTreeFlatDataSource,
} from '@angular/material/tree';
import { WindowService } from 'src/shared/providers/window.service';
import { AddressModel } from 'src/models/address.model';
import {
  ItemNode,
  FlatNode,
  ChipFilter,
} from 'src/models/schedule.model';
export interface MaintenanceFilters {
  property: string[];
}
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';

@Component({
    templateUrl: 'recurring-tasks-filter.html',
    styleUrls: ['./recurring-tasks-filter.scss'],
    selector: 'tidy-recurring-tasks-filter',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class RecurringTasksFilter implements OnInit, OnChanges {

  @Input() loaded: boolean;
  @Input() addresses: AddressModel[];
  @Input() selectedFilters: MaintenanceFilters;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSearch = new EventEmitter<string>();

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFilter = new EventEmitter<{
    selectedFilters: MaintenanceFilters;
    lastSelectedFilter: string;
  }>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onAddTask = new EventEmitter<void>();
  private _transformer = (node: ItemNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      data: node,
    });
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  form: UntypedFormGroup;
  chips: ChipFilter[] = [];
  filterNamesReferences = {
    'Property': 'property',
  };
  pros: any[] = [];


  boundAddMaintenance = this.addMaintenance.bind(this);

constructor(
    private fb: FormBuilder,
    public windowService: WindowService,
    private navCtrl: CustomNavController
  ) {
    this.form = this.fb.group({
      addresses: ['', Validators.required],
      search: [''],
    });
    this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val) => this.onSearch.emit(val));
  }

  async ngOnInit(): Promise<void> {
    this.getTreeData();
    this.selectedFilters = {
      property: [],
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.addresses) {
      this.getTreeData();
    }
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  getTreeData(): void {
    const treeData = [
      {
        name: 'Property',
        children: this.getAddressTree(),
      },
    ];
    treeData.forEach((parent, index) => {
      if (parent.children?.length === 0) {
        treeData.splice(index, 1);
      }
      parent.children?.forEach((child) => {
        child.parentId = index;
      });
    });
    this.dataSource.data = treeData;
  }

  getAddressTree(): ItemNode[] {
    return this.addresses
      ?.map((address) => ({
          name: address['address_name'] || address['address1'],
          value: address.id.toString(),
          checked: false,
        }))
      .filter((address) => address?.name);
  }

  checkNode(node: ItemNode, event: MatCheckboxChange): void {
    node.checked = event.checked;
    this.updateFilters(node, event.checked);
  }

  getNodeVariables(node: ItemNode) {
    const foundParentName = this.dataSource.data[node.data.parentId].name;
    const foundNodeValue = this.dataSource.data[
      node.data.parentId
    ].children.find((child) => child.name === node.name).value;
    const matchedParentName = this.filterNamesReferences[foundParentName];
    return { foundParentName, foundNodeValue, matchedParentName };
  }

  updateFilters(node: ItemNode, isEnabled: boolean): void {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } =
      this.getNodeVariables(node);
    const chip: ChipFilter = {
      displayParentName: foundParentName,
      parentName: matchedParentName,
      name: node.name,
      value: foundNodeValue,
      icon: 'home',
      color: this.getChipColor(foundNodeValue),
    };
    if (isEnabled) {
      this.addChip(chip);
    } else {
      this.removeChip(chip, foundParentName);
    }
  }

  private getChipColor(value: any): string {
    const address = this.addresses?.find((a) => a.id.toString() === value);
    return address?.color || '#1E2125';
  }

  addChip(chip: ChipFilter): void {
    this.selectedFilters[chip.parentName].push(chip.value);
    this.chips.push(chip);
    this.onFilter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  updateTree(): void {
    this.dataSource.data.forEach((parent) => {
      parent.children.forEach((child) => {
        if (
          this.selectedFilters[
            this.filterNamesReferences[parent.name]
          ].includes(child.value)
        ) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter, parentName: string): void {
    this.selectedFilters[chip.parentName] = this.selectedFilters[
      chip.parentName
    ].filter((value) => value !== chip.value);
    this.chips = this.chips.filter(
      (item) => !(item.parentName === chip.parentName && item.value === chip.value)
    );
    this.onFilter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  getNodeChecked(node: ItemNode): boolean {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } = this.getNodeVariables(node);
    return this.selectedFilters[matchedParentName].includes(foundNodeValue);
  }

  addMaintenance(): void {
    this.onAddTask.emit();
  }

  goBack() {
    this.navCtrl.navigateBack('maintenance');
  }
}
